
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  BarChart2, 
  ArrowUpCircle, 
  ArrowDownCircle, 
  Clock, 
  DollarSign,
  Briefcase,
  BarChart4,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { stockDatasets } from '../data/sampleData';
import IBKRTradingConfig from './IBKRTradingConfig';
import { formatCurrency, formatPercentChange } from '../utils/stockUtils';

const Trading = () => {
  const [ibkrConnected, setIbkrConnected] = useState(false);
  const [tradeHistory, setTradeHistory] = useState<any[]>([]);
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [selectedStock, setSelectedStock] = useState(stockDatasets[0]);
  const [quantity, setQuantity] = useState(1);
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [limitPrice, setLimitPrice] = useState(selectedStock.currentValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTrade = (action: 'buy' | 'sell') => {
    if (!ibkrConnected) return;
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      const newOrder = {
        id: `order-${Math.floor(Math.random() * 1000000)}`,
        timestamp: new Date().toISOString(),
        symbol: selectedStock.symbol,
        action: action,
        quantity: quantity,
        price: orderType === 'market' ? selectedStock.currentValue : limitPrice,
        type: orderType,
        status: Math.random() > 0.2 ? 'executed' : 'pending',
        total: (orderType === 'market' ? selectedStock.currentValue : limitPrice) * quantity
      };
      
      if (newOrder.status === 'executed') {
        setTradeHistory(prev => [newOrder, ...prev]);
      } else {
        setPendingOrders(prev => [newOrder, ...prev]);
      }
      
      setIsSubmitting(false);
    }, 1500);
  };

  const cancelOrder = (orderId: string) => {
    setPendingOrders(prev => prev.filter(order => order.id !== orderId));
  };

  if (!ibkrConnected) {
    return (
      <div className="max-w-md mx-auto mt-8">
        <IBKRTradingConfig onConnected={setIbkrConnected} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="col-span-2 bg-card border rounded-xl p-6 space-y-6">
          <h2 className="text-2xl font-semibold">Trading Terminal</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Select Instrument</h3>
              <select 
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={selectedStock.id}
                onChange={(e) => {
                  const stock = stockDatasets.find(s => s.id === e.target.value);
                  if (stock) {
                    setSelectedStock(stock);
                    setLimitPrice(stock.currentValue);
                  }
                }}
              >
                {stockDatasets.map(stock => (
                  <option key={stock.id} value={stock.id}>{stock.name} ({stock.symbol})</option>
                ))}
              </select>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Price</span>
                <span className="text-sm font-medium">{formatCurrency(selectedStock.currentValue)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Today's Change</span>
                <span className={cn(
                  "text-sm font-medium",
                  selectedStock.changePercent >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {formatPercentChange(selectedStock.changePercent)}
                </span>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Order Details</h3>
              
              <div className="space-y-2">
                <label className="text-sm">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm">Order Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setOrderType('market')}
                    className={cn(
                      "p-2 rounded-md text-sm font-medium",
                      orderType === 'market' 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted text-muted-foreground hover:bg-muted/70"
                    )}
                  >
                    Market
                  </button>
                  <button
                    onClick={() => setOrderType('limit')}
                    className={cn(
                      "p-2 rounded-md text-sm font-medium",
                      orderType === 'limit' 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted text-muted-foreground hover:bg-muted/70"
                    )}
                  >
                    Limit
                  </button>
                </div>
              </div>
              
              {orderType === 'limit' && (
                <div className="space-y-2">
                  <label className="text-sm">Limit Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(parseFloat(e.target.value) || selectedStock.currentValue)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-sm">Estimated Total</label>
                <div className="text-lg font-bold">
                  {formatCurrency((orderType === 'market' ? selectedStock.currentValue : limitPrice) * quantity)}
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <Button
              onClick={() => handleTrade('buy')}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowUpCircle size={16} className="mr-2" />
                  Buy {selectedStock.symbol}
                </>
              )}
            </Button>
            
            <Button
              onClick={() => handleTrade('sell')}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowDownCircle size={16} className="mr-2" />
                  Sell {selectedStock.symbol}
                </>
              )}
            </Button>
          </div>
        </div>
        
        <div className="bg-card border rounded-xl p-6 space-y-6">
          <h2 className="text-2xl font-semibold">Market Overview</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between px-4 py-3 bg-muted/30 rounded-lg">
              <div className="flex items-center">
                <BarChart2 size={20} className="text-primary mr-2" />
                <span className="text-sm font-medium">S&P 500</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-sm font-bold">4,932.46</span>
                <span className="text-xs text-green-600">+0.76%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between px-4 py-3 bg-muted/30 rounded-lg">
              <div className="flex items-center">
                <BarChart4 size={20} className="text-primary mr-2" />
                <span className="text-sm font-medium">NASDAQ</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-sm font-bold">17,053.02</span>
                <span className="text-xs text-green-600">+1.12%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between px-4 py-3 bg-muted/30 rounded-lg">
              <div className="flex items-center">
                <DollarSign size={20} className="text-primary mr-2" />
                <span className="text-sm font-medium">USD/EUR</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-sm font-bold">0.8931</span>
                <span className="text-xs text-red-600">-0.23%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between px-4 py-3 bg-muted/30 rounded-lg">
              <div className="flex items-center">
                <Briefcase size={20} className="text-primary mr-2" />
                <span className="text-sm font-medium">Portfolio Value</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-sm font-bold">$124,583.21</span>
                <span className="text-xs text-green-600">+2.4% today</span>
              </div>
            </div>
          </div>
          
          <div className="pt-4">
            <h3 className="text-sm font-medium mb-3">Market Hours</h3>
            <div className="flex items-center text-sm">
              <Clock size={16} className="text-muted-foreground mr-2" />
              <div className="flex flex-col">
                <span>NYSE: <span className="text-green-600 font-medium">Open</span></span>
                <span className="text-xs text-muted-foreground">Closes in 3h 24m</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card border rounded-xl overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Pending Orders</h3>
          </div>
          
          <div className="p-4">
            {pendingOrders.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                No pending orders
              </div>
            ) : (
              <div className="space-y-4">
                {pendingOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center">
                      <div className={cn(
                        "h-8 w-8 rounded-full flex items-center justify-center mr-3",
                        order.action === 'buy' ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                      )}>
                        {order.action === 'buy' ? <ArrowUpCircle size={16} /> : <ArrowDownCircle size={16} />}
                      </div>
                      <div>
                        <div className="font-medium">{order.action.toUpperCase()} {order.quantity} {order.symbol}</div>
                        <div className="text-xs text-muted-foreground">
                          {order.type.toUpperCase()} @ {formatCurrency(order.price)} • {new Date(order.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium mr-4">{formatCurrency(order.total)}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => cancelOrder(order.id)}
                        className="text-xs h-8"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-card border rounded-xl overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Trade History</h3>
          </div>
          
          <div className="p-4">
            {tradeHistory.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                No trade history
              </div>
            ) : (
              <div className="space-y-4">
                {tradeHistory.map((trade) => (
                  <div key={trade.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center">
                      <div className={cn(
                        "h-8 w-8 rounded-full flex items-center justify-center mr-3",
                        trade.action === 'buy' ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                      )}>
                        {trade.action === 'buy' ? <ArrowUpCircle size={16} /> : <ArrowDownCircle size={16} />}
                      </div>
                      <div>
                        <div className="font-medium">{trade.action.toUpperCase()} {trade.quantity} {trade.symbol}</div>
                        <div className="text-xs text-muted-foreground">
                          {trade.type.toUpperCase()} @ {formatCurrency(trade.price)} • {new Date(trade.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium">{formatCurrency(trade.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trading;
